"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, NavLink, Link, useLocation, Navigate } from 'react-router-dom'; // Import Navigate
import { AudioLines } from 'lucide-react';
import { supabase } from '@/lib/supabase';
// Sidebar (hamburger) removed per UX change
import { Button } from '@/components/ui/button';
import { Search, Store, Newspaper, ReceiptText, PlusCircle, ArrowLeft, Mail, Bell, Heart, MessageSquareText, Menu, Home, ChevronDown, Settings, ShoppingCart, Headset, AlertTriangle, WifiOff, Bike, Car, Truck } from '@/components/icons';
import { ShoppingBasket } from 'lucide-react';
import MinibidMIconWhite from './MinibidMIconWhite';
import { useSession } from '@/context/SessionContext';
import { useChatContext } from '@/context/ChatContext';
import { useNotifications } from '@/context/NotificationContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { useOrderSidebar } from '@/context/OrderSidebarContext';
import { useSearch } from '@/context/SearchContext';
import { useUserMenu } from '@/context/UserMenuContext';
import { useCategorySidebar } from '@/context/CategorySidebarContext';
import { useSettingsSidebar } from '@/context/SettingsSidebarContext';
import { useAddressManager } from '@/context/AddressManagerContext';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getOpenCartsForUser, checkoutCart, addCartItem, getOrCreateCartForStore } from '@/lib/supabase/carts';
import { getPendingOrdersCount } from '@/lib/supabase/orders';
import { getTempCartAsItems, addToTempCart } from '@/lib/tempCart';
import { getUserAddresses, UserAddress, upsertUserAddress } from '@/lib/supabase/userAddresses';
import { useGuestAddress, getStoredGuestAddress, clearGuestAddressFromStorage } from '@/hooks/useGuestAddress';
import { usePendingAuctionPayments } from '@/hooks/usePendingAuctionPayments';
import { createNotification } from '@/lib/supabase/notifications';
import { getNoteById, getProductById } from '@/lib/supabase';
import { getStoreById, getUserStores, createStore, type StoreItem } from '@/lib/supabase/stores';
import { getOrCreateAnonymousSupportSessionId } from '@/lib/anonymousSupportChat';
import { getOrCreateDMConversation } from '@/lib/supabase/chat';
import { formatOrderFriendly, formatAddressForDisplay, extractCityName, cn } from '@/lib/utils';
import { updateProfileRelevantLocation } from '@/lib/supabase/profiles';
import { useToast } from '@/components/ui/use-toast';
import { useThemeColor } from '@/hooks/useThemeColor';
import { applyAppearanceToRoot } from '@/lib/appearance';
import { resolveDefaultPrimaryHex } from '@/lib/appearanceDefaults';
import {
  ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT,
  ACTIVE_SHARED_CART_STATE_CHANGED,
  getActiveSharedCartThemePriority,
  getActiveCartSnapshotByStoreSlug,
  setActiveSharedCartThemePriority,
  getActiveSharedCartRole,
  getActiveSharedCartGuestOwnerColor,
  getActiveSharedCartCollaboratorColor,
} from '@/lib/activeCartPreference';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import KeyboardShortcutsFix from './KeyboardShortcutsFix';
import Logo from './Logo';
import { connectAudio } from './RadioVisualizer';
import CartSidebar from './cart/CartSidebar';
import { PaymentSidebar } from './PaymentSidebar';
import { OrderSidebar } from './OrderSidebar';
import { ProductSidebar } from './ProductSidebar';
import { NoteSidebar } from './NoteSidebar';
import { CreateNoteSidebar } from './CreateNoteSidebar';
import { FavoritesSidebar } from './FavoritesSidebar';
import { MinimizedNotesStack } from './MinimizedNotesStack';
import { MinimizedProductsStack } from './MinimizedProductsStack';
import { X, User as UserIcon } from '@/components/icons';
import { useRequestRideContext } from '@/context/RequestRideContext';
import { useDriversAvailability } from '@/context/DriversAvailabilityContext';
import { useNativeNotifications } from '@/hooks/useNativeNotifications';
import { useFavoritesSidebar } from '@/context/FavoritesSidebarContext';
import { useFavorites } from '@/hooks/useFavorites';
import { useActivitiesSidebar } from '@/context/ActivitiesSidebarContext';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { usePlaySounds } from '@/hooks/useSoundPlayer';
import { useSidebarEarcons } from '@/hooks/useEarcons';
import { useVibrate } from '@/hooks/useVibrate';
import { useRadioNowPlaying } from '@/hooks/useRadioNowPlaying';
import { useSmoothedNowPlaying } from '@/hooks/useSmoothedNowPlaying';
import { CitySelectorSidebar } from './CitySelectorSidebar';
import { useMusic } from '@/context/MusicContext';
import {
  CITY_LANDMARK_LOCATIONS,
  findCityLandmarkByDisplayName,
  findNearestCityLandmarkByCoords,
} from '@/lib/defaultLocations';
import {
  FOUNDER_CHALLENGE_DEFAULT_HOURS,
  hasFounderBadgeForCity,
  startFounderChallenge,
} from '@/lib/founderProgram';

const getRouteKey = (pathname: string) => {
  if (pathname === '/notas') return 'feed-notas';
  if (pathname === '/tiendas') return 'feed-tiendas';
  if (pathname.startsWith('/buscar')) return 'search-base';
  if (pathname.startsWith('/tiendas/')) return 'store-detail';
  return pathname;
};

const JACARANDA_HEX = '#a259ff';
const DULCE_DE_LECHE_HEX = '#f97316';
const RADIO_STATION_ARTWORK = '/brand/pwa-logo.png?v=20260525b';
const MOBILE_HEADER_OFFSET_BREATHING_ROOM = 7;
const MOBILE_HEADER_OFFSET_FALLBACK = 'calc(env(safe-area-inset-top, 0px) + 105px)';

const buildRadioArtwork = (artUrl?: string) => {
  const normalized = typeof artUrl === 'string' ? artUrl.trim() : '';
  const baseSrc = normalized || RADIO_STATION_ARTWORK;
  const baseType = baseSrc.endsWith('.png') ? 'image/png' : 'image/jpeg';

  return [96, 128, 192, 256, 384, 512].map((size) => ({
    src: baseSrc,
    sizes: `${size}x${size}`,
    type: baseType,
  }));
};

const isDulceDeLeche = (color: string | null | undefined): boolean => {
  if (!color) return true; // null/undefined = default = dulce de leche
  return color.toLowerCase() === DULCE_DE_LECHE_HEX;
};

const normalizeHex = (color: string | null | undefined): string | null => {
  if (!color || typeof color !== 'string') return null;
  const normalized = color.trim().toLowerCase();
  return normalized || null;
};

interface RentalStoreAssignmentState {
  orderId: string;
  stores: StoreItem[];
  selectedStoreId: string;
}

const EXCLUDED_STORE_ROOTS = new Set([
  'inicio',
  'auth',
  'login',
  'signup',
  'chats',
  'chat',
  'tour',
  'map',
  'mapa',
  'checkout',
  'collections',
  'notificaciones',
  'configuracion',
  'transporte',
  'search-results',
  'print-room',
  'activity-log',
  'cargafacil',
  'notas',
  'orden',
  'products',
  'edit-product',
  'tiendas',
  'mercadopago',
  '24hs',
]);

const extractStoreSlugFromPathname = (pathname: string): string | null => {
  const segment = (pathname || '').split('/').filter(Boolean)[0] || '';
  if (!segment) return null;
  if (EXCLUDED_STORE_ROOTS.has(segment)) return null;
  return segment;
};

const parseStoredAppearance = (): any | null => {
  if (typeof window === 'undefined') return null;

  const candidates = [
    localStorage.getItem('appearance-settings'),
    localStorage.getItem('guest-appearance-settings'),
  ];

  for (const raw of candidates) {
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      // Ignore malformed appearance payloads.
    }
  }

  return null;
};

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Get current location
  const { session, loading, profile, relevantLocation, setRelevantLocation, refreshProfile } = useSession();
  const { setAvailability: setDriversAvailability } = useDriversAvailability();
  const { isPlaying: isRadioPlaying, currentSrc, audioRef, startRadioPlayback, stopPlayback } = useMusic();
  const { isDesktop, headerHeight, bottomNavHeight } = useHeaderOffset();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // Reactive state driven by audio element events — avoids stale reads of .paused
  const [isAudioActuallyPlaying, setIsAudioActuallyPlaying] = useState(false);
  const isMinibid24hsRoute = location.pathname === '/24hs';
  const isFayeRoute = location.pathname === '/radio' || location.pathname.startsWith('/radio/');
  const isChangasRoute = location.pathname === '/transporte' || location.pathname.startsWith('/transporte/');
  const isRadioStreamActive = Boolean(
    currentSrc && (
      currentSrc.includes('radio.minibid.org/listen/minibid/') ||
      currentSrc.includes('/stream-radio')
    )
  );
  const isRadioPlaybackActive = isRadioPlaying && isRadioStreamActive;
  const isRadioConnecting = isRadioPlaying && !isAudioActuallyPlaying;
  const radioReconnectAttemptRef = useRef(0);
  const shouldRunRadioTicker = true;
  const { nowPlaying: rawRadioNowPlaying, refreshNowPlaying } = useRadioNowPlaying({ enabled: shouldRunRadioTicker });
  const radioNowPlaying = useSmoothedNowPlaying(rawRadioNowPlaying, isAudioActuallyPlaying, 7000);
  const radioSongTitle = String(radioNowPlaying?.title || '').trim();

  // Listen to the audio element's playing/pause/waiting events so that
  // "Encendiendo..." shows while the stream is buffering and transitions
  // to the song title as soon as audio actually starts producing sound.
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    const onPlaying = () => setIsAudioActuallyPlaying(true);
    const onNotPlaying = () => setIsAudioActuallyPlaying(false);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onNotPlaying);
    audio.addEventListener('waiting', onNotPlaying);
    audio.addEventListener('ended', onNotPlaying);
    // Sync initial state
    setIsAudioActuallyPlaying(!audio.paused);
    return () => {
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onNotPlaying);
      audio.removeEventListener('waiting', onNotPlaying);
      audio.removeEventListener('ended', onNotPlaying);
    };
  }, [audioRef?.current]);

  useEffect(() => {
    if (!isRadioPlaying || isAudioActuallyPlaying) {
      radioReconnectAttemptRef.current = 0;
      return;
    }

    const retryId = window.setTimeout(() => {
      if (!isRadioPlaying || isAudioActuallyPlaying) return;
      if (radioReconnectAttemptRef.current >= 1) return;

      radioReconnectAttemptRef.current += 1;
      void startRadioPlayback({ forceReload: true });
      void refreshNowPlaying();
    }, 6500);

    return () => {
      window.clearTimeout(retryId);
    };
  }, [isRadioPlaying, isAudioActuallyPlaying, startRadioPlayback, refreshNowPlaying]);

  const renderRadioNavLabel = useCallback((compact: boolean) => {
    const wrapperClass = compact
      ? 'flex items-center justify-center w-full min-w-0 max-w-full overflow-hidden whitespace-nowrap'
      : 'flex items-center justify-center w-full min-w-0 max-w-full overflow-hidden whitespace-nowrap';
    const contentClass = 'block w-full overflow-hidden whitespace-nowrap text-center pointer-events-none';

    // Show "Encendiendo..." while connecting but not yet playing
    if (isRadioPlaying && !isAudioActuallyPlaying) {
      return (
        <span className={cn(wrapperClass, 'font-black tracking-tight leading-none radio-navbar-pulse-target')}>
          <span className={contentClass}>
            <span className="radio-navbar-marquee-loop radio-navbar-marquee-loop--delayed inline-flex w-max">
              <span className="radio-navbar-marquee-copy inline-flex items-center whitespace-nowrap">Encendiendo...&nbsp;&nbsp;•&nbsp;&nbsp;</span>
              <span className="radio-navbar-marquee-copy inline-flex items-center whitespace-nowrap" aria-hidden>Encendiendo...&nbsp;&nbsp;•&nbsp;&nbsp;</span>
            </span>
          </span>
        </span>
      );
    }

    if (!isRadioPlaybackActive) {
      return (
        <span className={cn(wrapperClass, 'font-black tracking-tight leading-none radio-navbar-pulse-target')}>
          <span className={contentClass}>Radio</span>
        </span>
      );
    }

    // Show song title once audio is playing
    if (radioSongTitle && isAudioActuallyPlaying) {
      return (
        <span className={cn(wrapperClass, 'font-black tracking-tight leading-none radio-navbar-pulse-target')}>
          <span className={contentClass}>
            <span className="radio-navbar-marquee-loop radio-navbar-marquee-loop--delayed inline-flex w-max">
              <span className="radio-navbar-marquee-copy inline-flex items-center whitespace-nowrap">{radioSongTitle}&nbsp;&nbsp;•&nbsp;&nbsp;</span>
              <span className="radio-navbar-marquee-copy inline-flex items-center whitespace-nowrap" aria-hidden>{radioSongTitle}&nbsp;&nbsp;•&nbsp;&nbsp;</span>
            </span>
          </span>
        </span>
      );
    }

    return (
      <span className={cn(wrapperClass, 'font-black tracking-tight leading-none radio-navbar-pulse-target')}>
        <span className={contentClass}>Radio</span>
      </span>
    );
  }, [isRadioPlaybackActive, radioSongTitle, isAudioActuallyPlaying, isRadioPlaying]);

  const toggleRadioPlaybackFromIcon = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isRadioPlaying) {
      stopPlayback();
      return;
    }

    const audio = audioRef?.current;
    if (audio) {
      const nodes = connectAudio(audio);
      const ctx = nodes?.ctx;
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch((err) => {
          console.warn('[MainLayout] Failed to resume radio AudioContext from antenna button:', err);
        });
      }
    }

    void refreshNowPlaying();
    window.setTimeout(() => {
      void refreshNowPlaying();
    }, 1000);

    void startRadioPlayback({ forceReload: true });
  }, [audioRef, isRadioPlaying, startRadioPlayback, stopPlayback, refreshNowPlaying]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return;

    const isStandalone = (() => {
      if (typeof window === 'undefined') return false;
      return window.matchMedia('(display-mode: standalone)').matches || Boolean((navigator as any).standalone);
    })();

    const applyMediaSessionState = () => {
      const metadataSource = rawRadioNowPlaying || radioNowPlaying;

      try {
        const MetadataClass = (window as any).MediaMetadata;
        if (MetadataClass) {
          const title = String(metadataSource?.title || '').trim() || 'Minibid Radio';
          const artist = String(metadataSource?.artist || '').trim() || (metadataSource?.isLive ? (metadataSource?.streamerName || 'En vivo') : 'AutoDJ');
          navigator.mediaSession.metadata = new MetadataClass({
            title,
            artist,
            album: metadataSource?.isLive ? 'Minibid Radio · En vivo' : 'Minibid Radio',
            artwork: buildRadioArtwork(metadataSource?.art),
          });
        }
      } catch (_) {
        // Ignore metadata assignment errors.
      }

      try {
        navigator.mediaSession.playbackState = isAudioActuallyPlaying ? 'playing' : 'paused';
      } catch (_) {
        // Ignore playbackState assignment errors.
      }
    };

    const handlePlay = () => {
      void (async () => {
        await startRadioPlayback({ forceReload: false });
        void refreshNowPlaying();
        window.setTimeout(() => {
          void refreshNowPlaying();
        }, 1000);
      })();
    };

    const handlePause = () => {
      stopPlayback();
      applyMediaSessionState();
    };

    applyMediaSessionState();

    try {
      navigator.mediaSession.setActionHandler('play', handlePlay);
      navigator.mediaSession.setActionHandler('pause', handlePause);
      navigator.mediaSession.setActionHandler('stop', null);
      navigator.mediaSession.setActionHandler('previoustrack', null);
      navigator.mediaSession.setActionHandler('nexttrack', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekto', null);
    } catch (_) {
      // Ignore action handler errors on partial implementations.
    }

    const reanchor = () => {
      applyMediaSessionState();
      if (isRadioStreamActive || isAudioActuallyPlaying) {
        void refreshNowPlaying();
      }
    };

    window.addEventListener('pageshow', reanchor);
    window.addEventListener('focus', reanchor);
    document.addEventListener('visibilitychange', reanchor);

    const keepAliveId = isStandalone
      ? window.setInterval(reanchor, 8000)
      : null;

    return () => {
      window.removeEventListener('pageshow', reanchor);
      window.removeEventListener('focus', reanchor);
      document.removeEventListener('visibilitychange', reanchor);
      if (keepAliveId !== null) {
        window.clearInterval(keepAliveId);
      }

      try {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('stop', null);
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler('seekforward', null);
        navigator.mediaSession.setActionHandler('seekto', null);
      } catch (_) {
        // Ignore cleanup errors.
      }
    };
  }, [rawRadioNowPlaying?.title, rawRadioNowPlaying?.artist, rawRadioNowPlaying?.art, rawRadioNowPlaying?.isLive, rawRadioNowPlaying?.streamerName, radioNowPlaying?.title, radioNowPlaying?.artist, radioNowPlaying?.art, radioNowPlaying?.isLive, radioNowPlaying?.streamerName, isAudioActuallyPlaying, isRadioStreamActive, startRadioPlayback, stopPlayback, refreshNowPlaying]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return;

    const setPositionState = (navigator.mediaSession as any).setPositionState;
    if (typeof setPositionState !== 'function') return;

    const applyPosition = () => {
      const timelineSource = rawRadioNowPlaying || radioNowPlaying;
      const duration = Number(timelineSource?.durationSec || 0);
      const elapsed = Number(timelineSource?.elapsedSec || 0);
      const remaining = Number(timelineSource?.remainingSec || 0);
      const fetchedAt = Number(timelineSource?.fetchedAt || 0);

      if (!(duration > 0) || !Number.isFinite(duration)) return;

      const fromRemaining = Number.isFinite(remaining) ? duration - remaining : NaN;
      let position = Number.isFinite(fromRemaining) ? fromRemaining : elapsed;
      if (!Number.isFinite(position)) {
        position = 0;
      }

      if (isAudioActuallyPlaying && fetchedAt > 0) {
        position += (Date.now() - fetchedAt) / 1000;
      }

      const maxPosition = Math.max(0, duration - 0.25);
      const safePosition = Math.max(0, Math.min(position, maxPosition));

      try {
        setPositionState({
          duration,
          playbackRate: 1,
          position: safePosition,
        });
      } catch (_) {
        // Ignore timeline errors on partial Safari implementations.
      }
    };

    applyPosition();

    const timerId = isAudioActuallyPlaying
      ? window.setInterval(applyPosition, 1000)
      : null;

    return () => {
      if (timerId !== null) {
        window.clearInterval(timerId);
      }
    };
  }, [rawRadioNowPlaying?.elapsedSec, rawRadioNowPlaying?.durationSec, rawRadioNowPlaying?.remainingSec, rawRadioNowPlaying?.fetchedAt, radioNowPlaying?.elapsedSec, radioNowPlaying?.durationSec, radioNowPlaying?.remainingSec, radioNowPlaying?.fetchedAt, isAudioActuallyPlaying]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleActiveCartChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ isShared?: boolean }>;
      if (typeof customEvent.detail?.isShared === 'boolean') {
        setActiveSharedCartThemePriority(customEvent.detail.isShared);
      }
    };

    window.addEventListener('cart-active-changed', handleActiveCartChanged as EventListener);
    return () => {
      window.removeEventListener('cart-active-changed', handleActiveCartChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    const storeSlug = extractStoreSlugFromPathname(location.pathname);
    if (!storeSlug) return;

    const activeSnapshot = getActiveCartSnapshotByStoreSlug(storeSlug);
    if (!activeSnapshot) return;
    setActiveSharedCartThemePriority(Boolean(activeSnapshot.isShared));
  }, [location.pathname]);

  
  // Update theme-color meta tag based on route
  useThemeColor();

  // Cache the last applied appearance to avoid unnecessary resets
  const lastAppliedAppearanceRef = useRef<any>(null);
  const hasInitializedAppearanceRef = useRef(false);

  useEffect(() => {
    const applyPrioritySharedCartTheme = () => {
      const hasSharedPriorityCart = getActiveSharedCartThemePriority();

      if (hasSharedPriorityCart) {
        const role = getActiveSharedCartRole();
        const storedAppearance = parseStoredAppearance();
        const ownColor = normalizeHex(storedAppearance?.customPrimary) || normalizeHex(resolveDefaultPrimaryHex());

        // Player 1 / Player 2 hierarchy:
        // 1) Owner color leads for both.
        // 2) Collaborative fallback to jacaranda only when both sides are dulce de leche.
        if (role === 'owner') {
          const collaboratorColor = normalizeHex(getActiveSharedCartCollaboratorColor());
          if (isDulceDeLeche(ownColor)) {
            applyAppearanceToRoot({ palette: 'custom', customPrimary: JACARANDA_HEX, customAccent: JACARANDA_HEX });
            return;
          }

          const ownerColor = ownColor || normalizeHex(resolveDefaultPrimaryHex()) || DULCE_DE_LECHE_HEX;
          applyAppearanceToRoot({ palette: 'custom', customPrimary: ownerColor, customAccent: ownerColor });
          return;
        }

        // Guest mirrors owner color unless both are dulce de leche -> jacaranda.
        const ownerColor = normalizeHex(getActiveSharedCartGuestOwnerColor()) || normalizeHex(resolveDefaultPrimaryHex());
        const guestColor = ownColor;
        if (isDulceDeLeche(ownerColor)) {
          applyAppearanceToRoot({ palette: 'custom', customPrimary: JACARANDA_HEX, customAccent: JACARANDA_HEX });
          return;
        }
        const effectiveOwnerColor = ownerColor || normalizeHex(resolveDefaultPrimaryHex()) || DULCE_DE_LECHE_HEX;
        applyAppearanceToRoot({
          palette: 'custom',
          customPrimary: effectiveOwnerColor,
          customAccent: effectiveOwnerColor,
        });
        return;
      }

      // CRITICAL FIX: If stored appearance exists, always apply it
      // Don't fall back to default unless we're on first initialization
      const storedAppearance = parseStoredAppearance();
      if (storedAppearance) {
        applyAppearanceToRoot(storedAppearance);
        lastAppliedAppearanceRef.current = storedAppearance;
        hasInitializedAppearanceRef.current = true;
        return;
      }

      // Only apply default if:
      // 1. We haven't initialized yet (first load), OR
      // 2. There's explicitly no stored appearance at all
      // This prevents resetting away from custom themes during scroll
      if (!hasInitializedAppearanceRef.current) {
        applyAppearanceToRoot({
          palette: 'custom',
          customPrimary: resolveDefaultPrimaryHex(),
        });
        hasInitializedAppearanceRef.current = true;
      }
      // Otherwise, keep whatever appearance is currently applied
    };

    let throttleTimer: ReturnType<typeof setTimeout> | null = null;
    let rerunQueued = false;

    const scheduleApplyPrioritySharedCartTheme = () => {
      if (throttleTimer) {
        rerunQueued = true;
        return;
      }

      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        applyPrioritySharedCartTheme();

        if (rerunQueued) {
          rerunQueued = false;
          scheduleApplyPrioritySharedCartTheme();
        }
      }, 80);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key) return;
      if (event.key.startsWith('active_shared_cart_') || event.key === 'active_shared_cart_theme_priority') {
        scheduleApplyPrioritySharedCartTheme();
      }
      // Also handle appearance changes for guests and authenticated users
      if (event.key === 'guest-appearance-settings' || event.key === 'appearance-settings') {
        scheduleApplyPrioritySharedCartTheme();
      }
    };

    applyPrioritySharedCartTheme();
    window.addEventListener(ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT, scheduleApplyPrioritySharedCartTheme as EventListener);
    window.addEventListener(ACTIVE_SHARED_CART_STATE_CHANGED, scheduleApplyPrioritySharedCartTheme as EventListener);
    window.addEventListener('cart-active-changed', scheduleApplyPrioritySharedCartTheme as EventListener);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('appearance-settings-changed', scheduleApplyPrioritySharedCartTheme as EventListener);

    return () => {
      if (throttleTimer) {
        clearTimeout(throttleTimer);
        throttleTimer = null;
      }
      window.removeEventListener(ACTIVE_SHARED_CART_THEME_PRIORITY_CHANGED_EVENT, scheduleApplyPrioritySharedCartTheme as EventListener);
      window.removeEventListener(ACTIVE_SHARED_CART_STATE_CHANGED, scheduleApplyPrioritySharedCartTheme as EventListener);
      window.removeEventListener('cart-active-changed', scheduleApplyPrioritySharedCartTheme as EventListener);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('appearance-settings-changed', scheduleApplyPrioritySharedCartTheme as EventListener);
    };
  }, []);
  
  const { 
    openSidebar: openCheckoutSidebar, 
    closeSidebar: closeCheckoutSidebar, 
    sidebarOpen: checkoutSidebarOpen, 
    paymentSidebarOpen, 
    openPaymentSidebar,
    closePaymentSidebar, 
    paymentSidebarData, 
    openSidebar: reopenCheckoutSidebar,
    closeWithDelay: closeCheckoutWithDelay,
    cancelCloseDelay: cancelCheckoutCloseDelay,
    preferredStoreId
  } = useCheckout();
  const { openSidebar: openNotificationsSidebar, closeSidebar: closeNotificationsSidebar, sidebarOpen: notificationsSidebarOpen, unreadCount, closeWithDelay: closeNotificationsWithDelay, cancelCloseDelay: cancelNotificationsCloseDelay } = useNotifications();
  const { closeSidebar: closeChatSidebar, openSidebar: openChatSidebar, sidebarOpen: chatSidebarOpen, unreadCount: chatUnreadCount, toggleSidebar, closeWithDelay: closeChatWithDelay, cancelCloseDelay: cancelChatCloseDelay, openChatWindow } = useChatContext();
  const { openSidebar: openSearchSidebar, closeSidebar: closeSearchSidebar, sidebarOpen: searchSidebarOpen } = useSearch();
  const { openSidebar: openUserMenuSidebar, closeSidebar: closeUserMenuSidebar, sidebarOpen: userMenuSidebarOpen, closeWithDelay: closeUserMenuWithDelay, cancelCloseDelay: cancelUserMenuCloseDelay } = useUserMenu();
  const { open: openCategorySidebar, close: closeCategorySidebar, isOpen: categorySidebarOpen, closeWithDelay: closeCategoryWithDelay, cancelCloseDelay: cancelCategoryCloseDelay } = useCategorySidebar();
  const { open: openSettingsSidebar } = useSettingsSidebar();
  const { isOpen: favoritesSidebarOpen, open: openFavoritesSidebar, close: closeFavoritesSidebar } = useFavoritesSidebar();
  const { isOpen: activitiesSidebarOpen, openSidebar: openActivitiesSidebar, closeSidebar: closeActivitiesSidebar } = useActivitiesSidebar();
  const { noteSidebarOpen, setNoteSidebarOpen, selectedNote, setSelectedNote, productSidebarOpen, setProductSidebarOpen, selectedProduct, setSelectedProduct, addOpenedNote, addOpenedProduct, removeOpenedNote, removeOpenedProduct, toggleNoteMinimized, toggleProductMinimized, openedNotes, openedProducts } = useContentSidebar();
  const { open: openRideRequest, isOpen: rideRequestSidebarOpen } = useRequestRideContext();
  const { productSidebarOpenedFromOrder, setProductSidebarOpenedFromOrder } = useOrderSidebar();

  const isActividadesActive = activitiesSidebarOpen;
  const isTransporteActive = rideRequestSidebarOpen && !isActividadesActive;
  const is24hsActive = (location.pathname === '/24hs' || checkoutSidebarOpen) && !isActividadesActive && !isTransporteActive;
  const isRadioActive = location.pathname === '/radio' && !isActividadesActive && !isTransporteActive && !is24hsActive;

  const playBeep = usePlayBeep();
  const playSound = usePlaySounds();
  const vibrate = useVibrate();
  const addressManager = useAddressManager();
  const skipNextPaymentCloseReopenRef = useRef(false);
  const reopenPaymentAfterAddressRef = useRef(false);
  const pendingPaymentSidebarDataRef = useRef<any>(null);
  const cleanupTimeoutRef = React.useRef<NodeJS.Timeout|null>(null);
  const noteCleanupTimeoutRef = React.useRef<NodeJS.Timeout|null>(null);
  const hoverTimeoutRef = useRef<Record<string, NodeJS.Timeout | null>>({});
  const cartIdCacheRef = useRef<Record<string, string>>({}); // Cache cartId by storeId to avoid race conditions

  useEffect(() => {
    const markSkipReopen = () => {
      skipNextPaymentCloseReopenRef.current = true;
      reopenPaymentAfterAddressRef.current = true;
      pendingPaymentSidebarDataRef.current = paymentSidebarData || null;
    };

    window.addEventListener('payment-open-address-manager', markSkipReopen as EventListener);
    return () => {
      window.removeEventListener('payment-open-address-manager', markSkipReopen as EventListener);
    };
  }, [paymentSidebarData]);

  useEffect(() => {
    const handleAddressUpdated = () => {
      if (!reopenPaymentAfterAddressRef.current) return;

      reopenPaymentAfterAddressRef.current = false;
      const pendingData = pendingPaymentSidebarDataRef.current;
      pendingPaymentSidebarDataRef.current = null;
      if (!pendingData) return;

      addressManager.close();
      window.setTimeout(() => {
        openPaymentSidebar(pendingData);
      }, 220);
    };

    window.addEventListener('address-updated', handleAddressUpdated as EventListener);
    return () => {
      window.removeEventListener('address-updated', handleAddressUpdated as EventListener);
    };
  }, [addressManager, openPaymentSidebar]);

  // Inicializar notificaciones nativas
  useNativeNotifications();

  const clearHoverTimeout = (key: string) => {
    if (hoverTimeoutRef.current[key]) {
      clearTimeout(hoverTimeoutRef.current[key]!);
      hoverTimeoutRef.current[key] = null;
    }
  };

  const handleHoverOpen = (key: string, openFn: () => void, cancelCloseFn?: () => void) => {
    // Hover to open disabled - only click to open
    return;
  };
  
  useEffect(() => {
  }, [productSidebarOpen, selectedProduct]);

  useEffect(() => {
  }, [noteSidebarOpen, selectedNote]);

  // Cancel any pending cleanup when a new product is selected
  useEffect(() => {
    if (selectedProduct && cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
  }, [selectedProduct?.id]);

  // Cancel any pending cleanup when a new note is selected
  useEffect(() => {
    if (selectedNote && noteCleanupTimeoutRef.current) {
      clearTimeout(noteCleanupTimeoutRef.current);
      noteCleanupTimeoutRef.current = null;
    }
  }, [selectedNote?.id]);

  // Track opened notes in history
  useEffect(() => {
    if (selectedNote?.id && selectedNote?.title) {
      addOpenedNote(selectedNote.id, selectedNote.title);
    }
  }, [selectedNote?.id, selectedNote?.title, addOpenedNote]);

  // Track opened products in history
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct?.title) {
      addOpenedProduct(selectedProduct.id, selectedProduct.title);
    }
  }, [selectedProduct?.id, selectedProduct?.title, addOpenedProduct]);

  const { sidebarOpen: orderSidebarOpen } = useOrderSidebar();

  const sidebarEarconStates = useMemo(
    () => ({
      checkoutSidebarOpen,
      paymentSidebarOpen,
      notificationsSidebarOpen,
      chatSidebarOpen,
      searchSidebarOpen,
      userMenuSidebarOpen,
      categorySidebarOpen,
      favoritesSidebarOpen,
      activitiesSidebarOpen,
      noteSidebarOpen,
      productSidebarOpen,
      orderSidebarOpen,
    }),
    [
      checkoutSidebarOpen,
      paymentSidebarOpen,
      notificationsSidebarOpen,
      chatSidebarOpen,
      searchSidebarOpen,
      userMenuSidebarOpen,
      categorySidebarOpen,
      favoritesSidebarOpen,
      activitiesSidebarOpen,
      noteSidebarOpen,
      productSidebarOpen,
      orderSidebarOpen,
    ]
  );

  useSidebarEarcons(sidebarEarconStates);

  const [cartCount, setCartCount] = React.useState<number>(0);
  const [cartPulse, setCartPulse] = useState(false);
  const prevCartCount = useRef(cartCount);
  const [heartPulse, setHeartPulse] = useState(false);
  const prevFavoritesCount = useRef(0);
  const [chatPulse, setChatPulse] = useState(false);
  const prevChatUnreadCount = useRef(0);
  const [notificationPulse, setNotificationPulse] = useState(false);
  const prevNotificationUnreadCount = useRef(0);
  const { favorites } = useFavorites();
  const { toast } = useToast();
    // Pulse animation on cart badge when number changes
    useEffect(() => {
      if (prevCartCount.current !== cartCount) {
        setCartPulse(false);
        setTimeout(() => setCartPulse(true), 10);
        setTimeout(() => setCartPulse(false), 400);
        prevCartCount.current = cartCount;
      }
    }, [cartCount]);

  // Pulse animation on heart icon when favorites change
  useEffect(() => {
    const currentFavoritesCount = favorites.size;
    if (prevFavoritesCount.current !== currentFavoritesCount) {
      setHeartPulse(false);
      setTimeout(() => setHeartPulse(true), 10);
      setTimeout(() => setHeartPulse(false), 500);
      prevFavoritesCount.current = currentFavoritesCount;
    }
  }, [favorites.size]);

  // Pulse animation on chat badge when chat unread count changes
  useEffect(() => {
    if (prevChatUnreadCount.current !== chatUnreadCount) {
      setChatPulse(false);
      setTimeout(() => setChatPulse(true), 10);
      setTimeout(() => setChatPulse(false), 400);
      prevChatUnreadCount.current = chatUnreadCount;
    }
  }, [chatUnreadCount]);

  // Pulse animation on notification badge when notification unread count changes
  useEffect(() => {
    if (prevNotificationUnreadCount.current !== unreadCount) {
      setNotificationPulse(false);
      setTimeout(() => setNotificationPulse(true), 10);
      setTimeout(() => setNotificationPulse(false), 400);
      prevNotificationUnreadCount.current = unreadCount;
    }
  }, [unreadCount]);

  const [pendingOrdersCount, setPendingOrdersCount] = React.useState<number>(0);
  const { count: pendingAuctionPayments } = usePendingAuctionPayments(session?.user?.id);
  const [cartSheetOpen, setCartSheetOpen] = useState<boolean>(false);

  // Load cart count and pending orders for the header badge when session changes
  useEffect(() => {
    let mounted = true;
    // Reset cartId cache when session changes (prevents using wrong cart for different user)
    cartIdCacheRef.current = {};
    
    (async () => {
      try {
        if (session?.user?.id) {
          // Usuario logueado: cargar canasto desde BD
          const carts = await getOpenCartsForUser(session.user.id);
          const cartsCount = Array.isArray(carts) ? carts.length : 0;
          if (mounted) setCartCount(cartsCount || 0);
          
          const incompleteOrdersCount = await getPendingOrdersCount(session.user.id);
          if (mounted) setPendingOrdersCount(incompleteOrdersCount);
        } else {
          // Usuario no logueado: cargar canasto temporal
          const tempItems = getTempCartAsItems();
          const tempCartsCount = tempItems.length > 0 ? 1 : 0;
          if (mounted) setCartCount(tempCartsCount || 0);
          if (mounted) setPendingOrdersCount(0);
        }
      } catch (e) {
        console.warn('Failed to load cart count', e);
      }
    })();
    return () => { mounted = false; };
  }, [session?.user?.id]);

  // Listen for cart updates triggered elsewhere in the app
  useEffect(() => {
    const handler = async () => {
      try {
        if (session?.user?.id) {
          // Usuario logueado: cargar desde BD
          const carts = await getOpenCartsForUser(session.user.id);
          const cartsCount = Array.isArray(carts) ? carts.length : 0;
          setCartCount(cartsCount || 0);
          
          const incompleteOrdersCount = await getPendingOrdersCount(session.user.id);
          setPendingOrdersCount(incompleteOrdersCount);
        } else {
          // Usuario no logueado: cargar canasto temporal
          const tempItems = getTempCartAsItems();
          const tempCartsCount = tempItems.length > 0 ? 1 : 0;
          setCartCount(tempCartsCount || 0);
          setPendingOrdersCount(0);
        }
      } catch (e) {
        console.warn('cart-updated handler failed', e);
      }
    };
    window.addEventListener('cart-updated', handler as EventListener);
    const openHandler = () => setCartSheetOpen(true);
    window.addEventListener('open-cart-sidebar', openHandler as EventListener);
    return () => {
      window.removeEventListener('cart-updated', handler as EventListener);
      window.removeEventListener('open-cart-sidebar', openHandler as EventListener);
    };
  }, [session?.user?.id]);

  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [primaryAddress, setPrimaryAddress] = useState<UserAddress | null>(null);
  const [selectedCityHasFounderBadge, setSelectedCityHasFounderBadge] = useState<boolean>(false);
  const [onlineDriversCount, setOnlineDriversCount] = useState<number>(0);
  const [onlineDriversByType, setOnlineDriversByType] = useState<{
    bike: number;
    motorcycle: number;
    car: number;
    van: number;
  }>({ bike: 0, motorcycle: 0, car: 0, van: 0 });
  const [currentFlipIndex, setCurrentFlipIndex] = useState(0);
  const flipIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [citySidebarOpen, setCitySidebarOpen] = useState<boolean>(false);
  const [rentalStoreAssignment, setRentalStoreAssignment] = useState<RentalStoreAssignmentState | null>(null);
  const [isAssigningRentalStore, setIsAssigningRentalStore] = useState(false);
  const hasOnlineDrivers = onlineDriversCount > 0;

  interface SelectedCityLocation {
    city: string;
    displayName: string;
    latitude: number;
    longitude: number;
    isPredefined?: boolean;
  }

  const selectedCity = useMemo(() => {
    if (!relevantLocation) return 'Salta';

    const nearestByCoords = findNearestCityLandmarkByCoords(
      relevantLocation.latitude,
      relevantLocation.longitude
    );
    if (nearestByCoords) return nearestByCoords.city;

    if (relevantLocation.displayName) {
      const matchedLandmark = findCityLandmarkByDisplayName(relevantLocation.displayName);
      if (matchedLandmark) return matchedLandmark.city;

      const fallbackCity = extractCityName(relevantLocation);
      if (fallbackCity) return fallbackCity;
    }

    return 'Salta';
  }, [relevantLocation?.displayName, relevantLocation?.latitude, relevantLocation?.longitude]);

  const selectedCityId = useMemo(() => {
    return selectedCity
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');
  }, [selectedCity]);

  const refreshOnlineDriversCount = useCallback(async () => {
    if (!selectedCityId) {
      setOnlineDriversCount(0);
      setOnlineDriversByType({ bike: 0, motorcycle: 0, car: 0, van: 0 });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('transport_type')
        .eq('is_online', true)
        .eq('city_id', selectedCityId)
        .in('service_type', ['courier', 'driver']);

      if (error) {
        console.warn('[MainLayout] Could not load online drivers count:', error);
        setOnlineDriversCount(0);
        setOnlineDriversByType({ bike: 0, motorcycle: 0, car: 0, van: 0 });
        return;
      }

      const counts = { bike: 0, motorcycle: 0, car: 0, van: 0 };
      data?.forEach((profile: any) => {
        const type = profile.transport_type?.toLowerCase();
        if (type === 'bike') counts.bike++;
        else if (type === 'motorcycle') counts.motorcycle++;
        else if (type === 'car') counts.car++;
        else if (type === 'van') counts.van++;
      });

      const total = counts.bike + counts.motorcycle + counts.car + counts.van;
      setOnlineDriversCount(total);
      setOnlineDriversByType(counts);
    } catch (error) {
      console.warn('[MainLayout] Failed to refresh online drivers count:', error);
      setOnlineDriversCount(0);
      setOnlineDriversByType({ bike: 0, motorcycle: 0, car: 0, van: 0 });
    }
  }, [selectedCityId]);

  // Sincronizar drivers availability con el contexto global
  useEffect(() => {
    setDriversAvailability(onlineDriversByType);
  }, [onlineDriversByType, setDriversAvailability]);

  const vehicleFlipItems = useMemo(
    () => [
      // DOCUMENTATION: Changed to show only Bike vehicle - hiding Car and Truck for bike-only delivery
      { icon: Bike, label: 'Bici', count: onlineDriversByType.bike + onlineDriversByType.motorcycle },
      // { icon: Car, label: 'Auto', count: onlineDriversByType.car },
      // { icon: Truck, label: 'Van', count: onlineDriversByType.van },
    ],
    [onlineDriversByType]
  );

  const getFlipContent = useCallback((index: number) => {
    if (vehicleFlipItems.length === 0) {
      return { icon: Bike, label: 'Bici', count: 0 };
    }

    const normalizedIndex = ((index % vehicleFlipItems.length) + vehicleFlipItems.length) % vehicleFlipItems.length;
    return vehicleFlipItems[normalizedIndex];
  }, [vehicleFlipItems]);

  const activeVehicleFlipContent = getFlipContent(currentFlipIndex);
  const activeVehicleHasAvailability = Number(activeVehicleFlipContent.count) > 0;

  // Continuous flip animation for vehicle types
  useEffect(() => {
    if (vehicleFlipItems.length <= 1) {
      setCurrentFlipIndex(0);
      return;
    }

    let index = 0;
    setCurrentFlipIndex(0);

    flipIntervalRef.current = setInterval(() => {
      index = (index + 1) % vehicleFlipItems.length;
      setCurrentFlipIndex(index);
    }, 2500);

    return () => {
      if (flipIntervalRef.current) {
        clearInterval(flipIntervalRef.current);
        flipIntervalRef.current = null;
      }
    };
  }, [vehicleFlipItems.length]);

  useEffect(() => {
    void refreshOnlineDriversCount();

    const intervalId = window.setInterval(() => {
      void refreshOnlineDriversCount();
    }, 45000);

    const handleFocus = () => {
      void refreshOnlineDriversCount();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshOnlineDriversCount]);

  useEffect(() => {
    if (!selectedCityId) return;

    const channel = supabase
      .channel(`online-drivers-${selectedCityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `city_id=eq.${selectedCityId}`,
        },
        () => {
          void refreshOnlineDriversCount();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [selectedCityId, refreshOnlineDriversCount]);

  useEffect(() => {
    let mounted = true;

    const refreshFounderBadge = async () => {
      const hasBadge = await hasFounderBadgeForCity(selectedCity, session?.user?.id);
      if (mounted) setSelectedCityHasFounderBadge(hasBadge);
    };

    refreshFounderBadge();

    const handleFounderBadgeAwarded = async () => {
      await refreshFounderBadge();
    };

    window.addEventListener('founder-badge-awarded', handleFounderBadgeAwarded);
    return () => {
      mounted = false;
      window.removeEventListener('founder-badge-awarded', handleFounderBadgeAwarded);
    };
  }, [selectedCity, session?.user?.id]);

  const handleCitySelect = async (selection: SelectedCityLocation) => {
    const nextLocation = {
      // Persist only the selected city label as canonical relevant location name.
      displayName: selection.city,
      latitude: selection.latitude,
      longitude: selection.longitude,
    };

    try {
      localStorage.setItem('relevant_location', JSON.stringify(nextLocation));
      localStorage.setItem('city_selector_override', '1');
    } catch (error) {
      console.warn('[MainLayout] Could not persist city selector override locally:', error);
    }

    setRelevantLocation(nextLocation);
    setCitySidebarOpen(false);

    // Trigger consumers immediately; do not wait for async persistence.
    window.dispatchEvent(new CustomEvent('location-updated', { detail: nextLocation }));
    window.dispatchEvent(new CustomEvent('newsfeed-refresh'));
    window.dispatchEvent(new CustomEvent('newsfeed-refresh-requested'));
    window.dispatchEvent(new CustomEvent('browse-refresh-requested'));

    if (!selection.isPredefined) {
      const challenge = await startFounderChallenge({
        userId: session?.user?.id,
        city: selection.city,
        displayName: selection.displayName,
        latitude: selection.latitude,
        longitude: selection.longitude,
      });

      if (challenge) {
        toast({
          title: `Desafío fundador activado en ${selection.city}`,
          description: `Tenés ${FOUNDER_CHALLENGE_DEFAULT_HOURS} horas para crear la primera tienda y ganar el badge.`,
          onAction: () => navigate('/tiendas/mis-tiendas'),
        });
      }
    }

    // Keep persisted profile location in sync for logged users.
    if (session?.user?.id) {
      try {
        await updateProfileRelevantLocation(
          session.user.id,
          nextLocation.displayName,
          nextLocation.latitude,
          nextLocation.longitude
        );
        await refreshProfile();
      } catch (error) {
        console.warn('[MainLayout] Could not persist selected city to profile:', error);
      }
    }

  };

  useEffect(() => {
    if (!session?.user?.id) {
      setUserAddresses([]);
      // Use guest address from localStorage if not logged in
      const storedGuest = getStoredGuestAddress();
      setPrimaryAddress(storedGuest as any || null);
      return;
    }

    // When user logs in, migrate guest address if it exists
    const migrateGuestAddress = async () => {
      try {
        const guestAddr = getStoredGuestAddress();
        const userAddrs = await getUserAddresses(session.user.id);
        
        setUserAddresses(userAddrs);
        
        // If user has no addresses and there's a guest address, migrate it
        if (userAddrs.length === 0 && guestAddr) {
          const migratedAddr = await upsertUserAddress({
            user_id: session.user.id,
            label: guestAddr.label || 'Mi dirección',
            location: guestAddr.location,
            is_primary: true,
            floor: guestAddr.floor || null,
            apartment: guestAddr.apartment || null,
            notes: guestAddr.notes || null,
            altura: guestAddr.altura || null,
            streetName: guestAddr.streetName || null,
          });
          
          // Refresh addresses after migration
          const updatedAddrs = await getUserAddresses(session.user.id);
          setUserAddresses(updatedAddrs);
          setPrimaryAddress(updatedAddrs.find(a => a.is_primary) || updatedAddrs[0] || null);
          
          // Clear localStorage after successful migration
          clearGuestAddressFromStorage();
          
          window.dispatchEvent(new Event('address-updated'));
        } else {
          setPrimaryAddress(userAddrs.find(a => a.is_primary) || userAddrs[0] || null);
        }
      } catch (error) {
        console.error('[MainLayout] Error migrating guest address:', error);
        // Fallback: just load existing addresses
        getUserAddresses(session.user.id).then(addrs => {
          setUserAddresses(addrs);
          setPrimaryAddress(addrs.find(a => a.is_primary) || addrs[0] || null);
        }).catch(() => {});
      }
    };

    migrateGuestAddress();
  }, [session?.user?.id, addressManager.isOpen]); // Refetch when sidebar closes/changes

  useEffect(() => {
    const handleAddressUpdated = () => {
      if (!session?.user?.id) return;
      getUserAddresses(session.user.id).then(addrs => {
        setUserAddresses(addrs);
        setPrimaryAddress(addrs.find(a => a.is_primary) || addrs[0] || null);
      });
    };
    window.addEventListener('address-updated', handleAddressUpdated);
    return () => {
      window.removeEventListener('address-updated', handleAddressUpdated);
    };
  }, [session?.user?.id]);

  const isPerDayRentalCheckout = useCallback(async (cartId: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('cart_items')
      .select('id, products ( details, is_store_rental )')
      .eq('cart_id', cartId);

    if (error) throw error;

    const rows = Array.isArray(data) ? data : [];
    return rows.some((row: any) => {
      const productNode = Array.isArray(row?.products) ? row.products[0] : row?.products;
      // Primary: check the is_store_rental column
      if (productNode?.is_store_rental === true) return true;
      const details = productNode && typeof productNode === 'object' ? productNode.details : null;
      if (!details || typeof details !== 'object') return false;

      const billingMode = String((details as any).billing_mode || '').toLowerCase();
      const isStoreRental = Boolean((details as any).is_store_rental);
      const rentalTarget = String((details as any).rental_target || '').toLowerCase();

      if (isStoreRental) return true;
      if (billingMode !== 'per_day') return false;
      return rentalTarget === '' || rentalTarget.includes('store') || rentalTarget.includes('tienda');
    });
  }, []);

  const appendRentalAssignmentToOrder = useCallback(async (orderId: string, store: StoreItem) => {
    if (!orderId) return;

    const { data: existingOrder } = await supabase
      .from('orders')
      .select('notes')
      .eq('id', orderId)
      .single();

    const assignmentLine = `[rental_store_assignment] store_id=${store.id} store_slug=${store.slug} assigned_at=${new Date().toISOString()}`;
    const baseNotes = (existingOrder?.notes || '').trim();
    const mergedNotes = baseNotes ? `${baseNotes}\n${assignmentLine}` : assignmentLine;

    await supabase
      .from('orders')
      .update({ notes: mergedNotes })
      .eq('id', orderId);
  }, []);

  const handleConfirmRentalStoreAssignment = useCallback(async () => {
    if (!rentalStoreAssignment?.selectedStoreId) return;

    const selected = rentalStoreAssignment.stores.find((store) => store.id === rentalStoreAssignment.selectedStoreId);
    if (!selected) return;

    try {
      setIsAssigningRentalStore(true);
      await appendRentalAssignmentToOrder(rentalStoreAssignment.orderId, selected);
      toast({ title: 'Alquiler asignado', description: `Se asigno a ${selected.name}.` });
      setRentalStoreAssignment(null);
      navigate(`/${selected.slug}/tablero`);
    } catch (e) {
      console.error('Error assigning rental store:', e);
      toast({ title: 'Error', description: 'No pudimos asignar la tienda.', variant: 'destructive' });
    } finally {
      setIsAssigningRentalStore(false);
    }
  }, [appendRentalAssignmentToOrder, navigate, rentalStoreAssignment, toast]);

  const handlePaymentConfirm = async (
    buyerCbu: string,
    buyerBankName: string,
    buyerAccountHolder?: string,
    orderNotes?: string,
    paymentMethod?: string,
    deliveryData?: {
      delivery_fee?: number | null;
      delivery_distance?: number | null;
      delivery_pickup_address?: string | null;
      delivery_address?: string | null;
    },
    paymentProofUrl?: string | null
  ) => {
    if (!session?.user?.id || !paymentSidebarData) return;
    try {
      const hasPerDayRental = paymentSidebarData.cartId
        ? await isPerDayRentalCheckout(paymentSidebarData.cartId)
        : false;

      const res = await checkoutCart(session.user.id, paymentSidebarData.storeId, {
        buyerCbu,
        buyerBankName,
        buyerAccountHolder: buyerAccountHolder || undefined,
        paymentMethod: paymentMethod || 'bank_transfer',
        notes: orderNotes || null,
        payment_proof_url: paymentProofUrl || null,
        ...(deliveryData || {})
      }, {
        cartId: paymentSidebarData.cartId,
        sharedToken: paymentSidebarData.sharedToken || null,
      });
      const orderId = Array.isArray(res) ? (res[0]?.order_id || res[0]?.id) : (res?.order?.id || res?.order_id || res?.id);
      
      let orderRow = null;
      if (orderId) {
        try {
          const { data: orderData } = await supabase
            .from('orders')
            .select('id, created_at, order_number')
            .eq('id', orderId)
            .single();
          orderRow = orderData;
        } catch {}
      }

      // Guardar payment_proof_url si existe
      if (orderId && paymentProofUrl) {
        try {
          await supabase
            .from('orders')
            .update({ payment_proof_url: paymentProofUrl })
            .eq('id', orderId);
        } catch (err) {
          console.error('Error saving payment proof URL:', err);
        }
      }
      
      if (orderId && paymentSidebarData.storeId) {
        const friendlyNumber = orderRow?.order_number ? `#${orderRow.order_number}` : formatOrderFriendly(orderId, orderRow?.created_at);
        const { data: storeData } = await supabase
          .from('stores')
          .select('owner_id')
          .eq('id', paymentSidebarData.storeId)
          .single();
        if (storeData?.owner_id) {
          await createNotification(
            storeData.owner_id,
            'order_update',
            `El comprador informó la transferencia para la orden ${friendlyNumber}`,
            `/orders/${orderId}`
          );
        }
      }
      
      toast({ title: 'Orden creada', description: orderRow?.order_number ? `Orden #${orderRow.order_number} confirmada` : (orderId ? `Orden #${orderId.slice(0, 8)} confirmada` : 'Orden confirmada') });
      playSound('orderCreated');

      // Guardar rental_metadata en la orden para que el trigger la lea cuando el admin marque como paid
      // El trigger crea la tienda + notificación; el frontend la recibe por Realtime y muestra el toast
      if (orderId && paymentSidebarData.rentalMetadata) {
        const { error: rentalMetaErr } = await supabase
          .from('orders')
          .update({ rental_metadata: paymentSidebarData.rentalMetadata })
          .eq('id', orderId);
        if (rentalMetaErr) {
          console.error('[StoreRental] Failed to save rental_metadata to order:', rentalMetaErr);
        }
      } else if (orderId && hasPerDayRental) {
        try {
          const stores = await getUserStores();
          if (!stores.length) {
            const createdStore = await createStore({
              name: 'Nueva tienda',
              slug: '',
              description: null,
            });
            await appendRentalAssignmentToOrder(orderId, createdStore);
            toast({ title: 'Nueva tienda creada', description: 'Tu alquiler se asigno automaticamente a Nueva tienda.' });
            navigate(`/${createdStore.slug}/tablero`);
          } else {
            setRentalStoreAssignment({
              orderId,
              stores,
              selectedStoreId: stores[0].id,
            });
          }
        } catch (rentalFlowError) {
          console.error('Rental post-payment flow failed:', rentalFlowError);
          toast({
            title: 'Orden confirmada',
            description: 'Pago confirmado, pero no pudimos completar la asignacion de tienda.',
            variant: 'destructive'
          });
        }
      }

      closePaymentSidebar();
      window.dispatchEvent(new CustomEvent('cart-updated'));
      setTimeout(() => {
        reopenCheckoutSidebar();
      }, 300); // Esperar a que termine la animación de cierre completamente
    } catch (e) {
      toast({ title: 'Error', description: (e as any)?.message || '', variant: 'destructive' });
    }
  };

  const [buyerBankInfo, setBuyerBankInfo] = React.useState<any>(null);
  
  // Load buyer bank info when session changes
  useEffect(() => {
    const loadBuyerBankInfo = async () => {
      if (!session?.user?.id) return;
      try {
        const { data } = await supabase
          .from('profiles')
          .select('bank_cbu, bank_name, bank_account_holder, bank_accounts')
          .eq('id', session.user.id)
          .single();
        if (data) {
          setBuyerBankInfo(data);
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadBuyerBankInfo();
  }, [session?.user?.id]);

  const mainRef = React.useRef<HTMLDivElement | null>(null);
  const [hideHeader, setHideHeader] = useState(false);
  const lastScrollY = useRef(0);
  const hideDownThreshold = 25;
  const mobileRevealUpThreshold = 6;
  const desktopRevealUpThreshold = 12;
  const [lockedMobileTopOffsetPx, setLockedMobileTopOffsetPx] = useState<number | null>(null);

  useEffect(() => {
    if (isDesktop) {
      setLockedMobileTopOffsetPx(null);
      return;
    }

    if (hideHeader) return;

    const measured = Math.max(0, Number(headerHeight) || 0) + MOBILE_HEADER_OFFSET_BREATHING_ROOM;
    if (measured < 40) return;

    setLockedMobileTopOffsetPx((current) => {
      if (current === null) return measured;
      // Update only on meaningful shifts (rotation/layout change), avoiding tiny jitter.
      if (Math.abs(current - measured) >= 24) return measured;
      return current;
    });
  }, [headerHeight, hideHeader, isDesktop]);

  const mobileTopOffset = useMemo(() => {
    if (isDesktop) return '0px';
    if (hideHeader) return 'env(safe-area-inset-top, 0px)';

    if (lockedMobileTopOffsetPx !== null) return `${lockedMobileTopOffsetPx}px`;

    const measuredHeader = Math.max(0, Number(headerHeight) || 0) + MOBILE_HEADER_OFFSET_BREATHING_ROOM;
    return measuredHeader >= 40 ? `${measuredHeader}px` : MOBILE_HEADER_OFFSET_FALLBACK;
  }, [headerHeight, hideHeader, isDesktop, lockedMobileTopOffsetPx]);

  const mobileBottomSafeOffset = useMemo(() => {
    if (isDesktop) return '0px';
    const measuredBottomNav = Math.max(0, Number(bottomNavHeight) || 0);
    return `calc(env(safe-area-inset-bottom, 0px) + ${measuredBottomNav}px + 0.5rem)`;
  }, [bottomNavHeight, isDesktop]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.style.setProperty('--app-mobile-top-offset', mobileTopOffset);
    root.style.setProperty('--app-mobile-bottom-nav-height', isDesktop ? '0px' : `${Math.max(0, Number(bottomNavHeight) || 0)}px`);
    root.style.setProperty('--app-mobile-bottom-safe-offset', mobileBottomSafeOffset);
  }, [bottomNavHeight, isDesktop, mobileBottomSafeOffset, mobileTopOffset]);

  // Hide header on scroll down, show on scroll up.
  // /radio and /changas use inner scroll containers, so we listen in capture phase.
  useEffect(() => {
    if (isFayeRoute) {
      lastScrollY.current = 0;
      setHideHeader(false);
      return;
    }

    const useInnerScroll = isFayeRoute || isChangasRoute;

    const handleScroll = (event?: Event) => {
      let currentScrollY = window.scrollY;
      let pageHeight = document.documentElement.scrollHeight - window.innerHeight;

      if (useInnerScroll && event?.target instanceof HTMLElement) {
        const target = event.target;
        const isScrollableContainer = target.scrollHeight > target.clientHeight;

        if (isScrollableContainer) {
          currentScrollY = target.scrollTop;
          pageHeight = target.scrollHeight - target.clientHeight;
        }
      }

      const prevScrollY = lastScrollY.current;
      const diff = currentScrollY - prevScrollY;
      const isMobileViewport = window.innerWidth < 1024;
      const revealUpThreshold = isMobileViewport ? mobileRevealUpThreshold : desktopRevealUpThreshold;
      
      // Check if page has enough content to justify hiding navbar (>500px of scrollable content)
      const hasEnoughContent = pageHeight > 500;
      
      if (currentScrollY <= 40) {
        setHideHeader(false);
      } else {
        if (hasEnoughContent && diff > hideDownThreshold && currentScrollY > 80) {
          setHideHeader(true);
        } else if (diff < -revealUpThreshold) {
          // Social-style behavior: reveal header on the first intentional upward pull.
          setHideHeader(false);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    lastScrollY.current = 0;
    setHideHeader(false);

    if (useInnerScroll) {
      document.addEventListener('scroll', handleScroll, true);
      return () => document.removeEventListener('scroll', handleScroll, true);
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFayeRoute, isChangasRoute, isDesktop]);

  useEffect(() => {
    const handleHeaderVisibilityRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ hidden?: boolean }>;
      const requestedHidden = customEvent.detail?.hidden;
      if (typeof requestedHidden !== 'boolean') return;

      setHideHeader(requestedHidden);
      if (requestedHidden) {
        lastScrollY.current = Math.max(lastScrollY.current, window.scrollY);
      }
    };

    window.addEventListener('app-header-visibility', handleHeaderVisibilityRequest as EventListener);
    return () => {
      window.removeEventListener('app-header-visibility', handleHeaderVisibilityRequest as EventListener);
    };
  }, []);

  // Keep the top navbar fixed on /radio.
  useEffect(() => {
    if (!isFayeRoute) return;
    setHideHeader(false);
  }, [isFayeRoute]);
  
  // Save and restore scroll position for feed navigation
  useEffect(() => {
    // Restore scroll position when coming back to a feed page
    const savedScrollKey = `scroll-${location.pathname}`;
    const savedScroll = sessionStorage.getItem(savedScrollKey);
    
    if (savedScroll) {
      const scrollPos = parseInt(savedScroll, 10);
      setTimeout(() => {
        window.scrollTo(0, scrollPos);
      }, 100);
      sessionStorage.removeItem(savedScrollKey);
    }

    const handleSaveScroll = () => {
      if (location.pathname === '/notas' || location.pathname === '/tiendas' || location.pathname.startsWith('/buscar')) {
        sessionStorage.setItem(`scroll-${location.pathname}`, window.scrollY.toString());
      }
    };

    window.addEventListener('scroll', handleSaveScroll);
    return () => window.removeEventListener('scroll', handleSaveScroll);
  }, [location.pathname]);

  // Handle clicking on Help button to start chat with Minibid 24hs
  const handleHelpClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const MINIBID_24HS_STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';
      const store = await getStoreById(MINIBID_24HS_STORE_ID);
      
      if (!store) {
        console.warn('Could not find Minibid 24hs store');
        return;
      }

      // Close other sidebars
      closeNotificationsSidebar();
      closeChatSidebar();
      
      // Resolve effective auth state from Supabase to avoid stale UI state
      const { data: authData } = await supabase.auth.getUser();
      const loggedUser = authData?.user;

      // If user is not authenticated, use anonymous chat
      if (!loggedUser?.id) {
        const anonymousSessionId = getOrCreateAnonymousSupportSessionId(MINIBID_24HS_STORE_ID);
        
        // Open anonymous chat window
        openChatWindow({
          id: `help-anonymous-${anonymousSessionId}`,
          type: 'dm',
          title: store.name || 'Minibid 24hs',
          avatarUrl: store.avatar_url,
          isAnonymous: true,
          anonymousSessionId,
          anonymousStoreId: MINIBID_24HS_STORE_ID,
        });
      } else {
        // User is authenticated, open normal chat window
        const conversationId = await getOrCreateDMConversation(store.owner_id);
        openChatWindow({
          id: `dm-minibid-${store.owner_id}`,
          type: 'dm',
          conversationId,
          otherUserId: store.owner_id,
          otherUserSlug: store.slug,
          title: store.name || 'Minibid 24hs',
          avatarUrl: store.avatar_url,
        });
      }
    } catch (err) {
      console.error('Error initiating chat with Minibid 24hs:', err);
    }
  };

  // Keep hook order stable: declare local UI hooks here before any early returns

  if (loading) {
    return <div className="min-h-screen bg-background" aria-hidden="true" />;
  }

  // Permitir acceso público - no redirigir si no hay sesión

  

  return (
    <div className={cn(
      "flex flex-col min-h-screen bg-background w-full overflow-x-hidden",
      userMenuSidebarOpen && "avatar-menu-open"
    )}>
      <KeyboardShortcutsFix />
      <style>{`
        @keyframes radio-dish-pulse-opacity {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.74; }
        }
        .radio-sync-pulse .radio-antenna-button,
        .radio-sync-pulse .radio-navbar-pulse-target {
          animation: radio-dish-pulse-opacity 2.8s ease-in-out infinite;
        }
        @keyframes radio-navbar-marquee-loop {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .radio-navbar-marquee-loop {
          animation: radio-navbar-marquee-loop 16.8s linear infinite;
          will-change: transform;
        }
        .radio-navbar-marquee-loop--delayed {
          animation-delay: 0.3s;
          animation-fill-mode: both;
        }
      `}</style>
      
      {/* Dynamic Header */}
      <header 
        data-header-visible={!hideHeader}
        data-header-hidden={hideHeader}
        className={cn(
          "app-header relative isolate w-full overflow-x-hidden bg-primary text-primary-foreground z-[9000] lg:z-[9999] transition-[transform,opacity] duration-300 ease-out shrink-0 border-b border-white/20",
          isDesktop ? "sticky top-0 shadow-md" : "fixed top-0 left-0 right-0 rounded-b-[1.25rem]",
          hideHeader ? "-translate-y-full opacity-0" : "opacity-100"
        )}
      >
        {!isDesktop ? (

          /* Mobile Header */
          <div className="flex flex-col w-full px-3.5 max-[390px]:px-1.5 pt-[calc(8px+env(safe-area-inset-top,0px))] pb-2.5">
            <TooltipProvider>
              {/* Row 1: City, Address, Search, and Settings */}
              <div className="grid grid-cols-[minmax(0,1fr)_2.75rem] items-center gap-2 mb-2">
                <div
                  className={cn(
                    "min-w-0 w-full gap-1.5",
                    "grid grid-cols-[max-content_minmax(0,1fr)_minmax(0,1.25fr)] items-center"
                  )}
                >
                  <div className="relative min-w-0">
                    <button 
                      onClick={() => setCitySidebarOpen(!citySidebarOpen)}
                      className="flex items-center gap-2 px-2.5 h-9 rounded-full transition-colors text-sm text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/15 w-full max-w-[34vw] min-w-0"
                    >
                      <MinibidMIconWhite className="w-4 h-4 text-white/90 shrink-0" />
                      <span className="font-bold truncate">{selectedCity}</span>
                      {selectedCityHasFounderBadge && (
                        <span className="px-1 py-0.5 rounded-full text-[9px] font-black leading-none bg-white/20 border border-white/30">F</span>
                      )}
                      <ChevronDown className={cn("w-3 h-3 opacity-80 shrink-0 transition-transform duration-300", citySidebarOpen && "rotate-180")} />
                    </button>
                  </div>

                  <button
                    onClick={() => addressManager.isOpen ? addressManager.close() : addressManager.open()}
                    className="flex min-w-0 items-center gap-2 px-2.5 h-9 rounded-full transition-colors text-sm text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/15"
                  >
                    <Home className="w-4 h-4 text-white/90 shrink-0" />
                    <span className="truncate font-bold">
                      {primaryAddress ? formatAddressForDisplay(primaryAddress.location?.displayName) : 'Establecer dirección'}
                    </span>
                    <ChevronDown className={cn("w-3 h-3 opacity-80 shrink-0 transition-transform duration-300", addressManager.isOpen && "rotate-180")} />
                  </button>

                  <button 
                    onClick={openSearchSidebar}
                    className="flex min-w-0 w-full items-center gap-2 overflow-hidden bg-primary-foreground/10 hover:bg-primary-foreground/15 transition-colors h-9 px-3 rounded-full text-white text-sm"
                  >
                    <Search className="w-4 h-4 text-white/90 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-left text-white/90">Buscar cosas, actividad, lugares...</span>
                  </button>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button 
                      onClick={openSettingsSidebar}
                      className="relative justify-self-end p-2 text-white rounded-full transition-colors flex items-center justify-center h-9 w-9 bg-primary-foreground/10 hover:bg-primary-foreground/15"
                      aria-label="Configuracion"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent variant="navbar">Configuracion</TooltipContent>
                </Tooltip>
              </div>

            </TooltipProvider>
            {/* Row 2: Tab Selector */}
            <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-1.5 max-[390px]:gap-0.5 sm:gap-3 pt-0 pb-0 text-sm max-[390px]:text-[11px] font-black tracking-tight max-[390px]:tracking-normal">
              <div className={cn("relative min-w-0 h-[2.25rem] max-[390px]:h-[2rem] flex-shrink-0", isRadioConnecting && "radio-sync-pulse")}>
                <button
                  type="button"
                  aria-label={isRadioPlaying ? 'Pausar radio' : 'Reproducir radio'}
                  title={isRadioPlaying ? 'Pausar radio' : 'Reproducir radio'}
                  onClick={toggleRadioPlaybackFromIcon}
                  className={cn("radio-antenna-button absolute left-1 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 max-[390px]:h-6 max-[390px]:w-6 items-center justify-center rounded-full border border-white/20 bg-primary-foreground/15 transition-opacity duration-300", !isRadioPlaying && "opacity-50")}
                >
                  <AudioLines className="h-4 w-4 max-[390px]:h-3.5 max-[390px]:w-3.5" />
                </button>
                <button
                  onClick={() => {
                    navigate('/radio');
                  }}
                  className={cn(
                    "w-full h-full min-w-0 overflow-hidden whitespace-nowrap pl-9 max-[390px]:pl-8 pr-2 rounded-full border flex items-center justify-start flex-shrink-0 transition-all",
                    isRadioActive
                      ? "border-white/35 bg-primary-foreground/20 text-white font-black"
                      : "border-white/15 bg-primary-foreground/10 text-white/75 hover:text-white hover:bg-primary-foreground/15"
                  )}
                >
                  {renderRadioNavLabel(true)}
                </button>
              </div>
              <div className="relative min-w-0 h-[2.25rem] max-[390px]:h-[2rem]">
                <button
                  type="button"
                  aria-label="Abrir canasto"
                  title="Abrir canasto"
                  onClick={() => openCheckoutSidebar()}
                  className="absolute left-1 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 max-[390px]:h-6 max-[390px]:w-6 items-center justify-center rounded-full border border-white/20 bg-primary-foreground/15"
                >
                  <ShoppingCart className={cn("h-3.5 w-3.5 max-[390px]:h-3 max-[390px]:w-3 transition-opacity", !is24hsActive && "opacity-50")} />
                </button>
                <button
                  onClick={() => navigate('/24hs')}
                  className={cn(
                    "w-full h-full min-w-0 overflow-hidden whitespace-nowrap pl-9 max-[390px]:pl-8 pr-2 rounded-full border flex items-center justify-start transition-all",
                    is24hsActive
                      ? "border-white/35 bg-primary-foreground/20 text-white font-black"
                      : "border-white/15 bg-primary-foreground/10 text-white/75 hover:text-white hover:bg-primary-foreground/15"
                  )}
                >
                  <span className="block w-full truncate text-center">24hs</span>
                </button>
              </div>
              <div className="relative min-w-0 h-[2.25rem] max-[390px]:h-[2rem]">
                <button
                  onClick={() => openRideRequest('persona', 'traslados')}
                  className={cn(
                    "w-full h-full min-w-0 overflow-hidden pl-2 pr-2 rounded-full border flex items-center justify-center transition-all",
                    isTransporteActive ? "border-white/35 bg-primary-foreground/20 font-black" : "border-white/15 bg-primary-foreground/10",
                    activeVehicleHasAvailability ? "text-white" : "text-white/45",
                    !isTransporteActive && activeVehicleHasAvailability && "hover:text-white hover:bg-primary-foreground/15"
                  )}
                >
                  <span className="block w-full truncate text-center">Envíos</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Desktop Header */
          <div className="w-full bg-primary overflow-hidden">
            <div className="max-w-[1200px] mx-auto px-4 bg-primary w-full flex flex-col justify-center gap-2.5 pt-3 pb-4 h-full">
              {/* Row 1: Logo, Search, Actions */}
            <div className="flex items-center justify-between gap-3 md:gap-4 lg:gap-6 h-9">

              {/* Ciudad, direccion (solo logueado) y busqueda en ese orden */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="relative w-min">
                  <button 
                    onClick={() => setCitySidebarOpen(!citySidebarOpen)}
                    className="flex items-center gap-2 px-2.5 h-9 rounded-full transition-colors text-sm text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/15 w-min max-w-[220px]"
                  >
                    <MinibidMIconWhite className="w-4 h-4 text-white/90 shrink-0" />
                    <span className="font-bold truncate max-w-[150px]">{selectedCity}</span>
                    {selectedCityHasFounderBadge && (
                      <span className="px-1 py-0.5 rounded-full text-[9px] font-black leading-none bg-white/20 border border-white/30">F</span>
                    )}
                    <ChevronDown className={cn("w-3 h-3 opacity-80 shrink-0 transition-transform duration-300", citySidebarOpen && "rotate-180")}/>
                  </button>
                </div>

                <button 
                  onClick={() => addressManager.isOpen ? addressManager.close() : addressManager.open()}
                  className="flex items-center gap-2 px-3 h-9 rounded-full transition-colors text-primary-foreground bg-primary-foreground/10 hover:bg-primary-foreground/15 min-w-0 max-w-[280px]"
                >
                  <Home className="w-4 h-4 text-white/90 shrink-0" />
                  <span className="font-extrabold truncate">
                    {primaryAddress ? formatAddressForDisplay(primaryAddress.location?.displayName) : 'Establecer dirección'}
                  </span>
                  <ChevronDown className={cn("w-3 h-3 opacity-80 shrink-0 transition-transform duration-300", addressManager.isOpen && "rotate-180")} />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                <button 
                  onClick={openSearchSidebar}
                  className="flex w-full min-w-0 items-center gap-2 overflow-hidden bg-primary-foreground/10 hover:bg-primary-foreground/15 transition-colors h-9 px-3.5 rounded-full text-white text-sm shadow-sm"
                >
                  <Search className="w-4 h-4 shrink-0" />
                  <span className="min-w-0 flex-1 truncate text-left">Buscar cosas, actividad, lugares...</span>
                </button>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={(e) => { e.preventDefault(); if (favoritesSidebarOpen) { closeFavoritesSidebar(); } else { openFavoritesSidebar(); } }} 
                        className="icon-button relative h-9 w-9 p-1.5 text-white rounded-full transition-colors"
                        aria-label="Favoritos"
                      >
                        <Heart className="h-5 w-5" />
                        <span className={`notification-badge ${favorites.size > 0 ? 'badge-visible' : ''} ${heartPulse && favorites.size > 0 ? 'pulse' : ''}`}
                          aria-hidden={favorites.size === 0}
                        >
                          {favorites.size > 0 ? (favorites.size > 99 ? '99+' : favorites.size) : ''}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Favoritos</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={(e) => { e.preventDefault(); toggleSidebar(); }} 
                        className={`icon-button notification-trigger h-9 w-9 p-1.5 text-white rounded-full transition-colors ${chatSidebarOpen ? 'bg-white/35' : ''}`} 
                        aria-label="Mensajes"
                      >
                        <MessageSquareText className="h-5 w-5" />
                        <span className={`notification-badge ${chatUnreadCount > 0 ? 'badge-visible' : ''}`}
                          aria-hidden={chatUnreadCount === 0}
                          style={{ minWidth: chatUnreadCount > 99 ? '28px' : '20px' }}
                        >
                          {chatUnreadCount > 0 ? (chatUnreadCount > 99 ? '99+' : chatUnreadCount) : ''}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Mensajes</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        className={`icon-button notification-trigger h-9 w-9 p-1.5 text-white rounded-full transition-colors ${checkoutSidebarOpen ? 'bg-white/35' : ''}`} 
                        aria-label="Canasto"
                        onClick={(e) => {
                          e.preventDefault();
                          if (checkoutSidebarOpen) {
                            closeCheckoutSidebar();
                          } else {
                            closeNotificationsSidebar();
                            closeChatSidebar();
                            openCheckoutSidebar({ preferredStoreId: preferredStoreId || undefined });
                          }
                        }}
                      >
                        <ShoppingBasket className="h-5 w-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Pedidos</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={(e) => { e.preventDefault(); if (notificationsSidebarOpen) { closeNotificationsSidebar(); } else { openNotificationsSidebar(); } }} 
                        className={`icon-button notification-trigger h-9 w-9 p-1.5 text-white rounded-full transition-colors ${notificationsSidebarOpen ? 'bg-white/35' : ''}`} 
                        aria-label="Actividad"
                      >
                        <Bell className="h-5 w-5" />
                        <span className={`notification-badge ${unreadCount > 0 ? 'badge-visible' : ''}`}
                          aria-hidden={unreadCount === 0}
                        >
                          {unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : ''}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Actividad</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={openSettingsSidebar}
                        className={`icon-button h-9 w-9 p-1.5 text-white rounded-full transition-colors ml-1`} 
                        aria-label="Configuración"
                      >
                        <Settings className="h-5 w-5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Configuración</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={openUserMenuSidebar}
                        className="hidden md:flex items-center gap-2 h-9 px-2 rounded-full transition-colors ml-2 relative"
                      >
                        {session?.user?.id && (
                          <Avatar className="h-7 w-7 border border-white/20">
                            <AvatarImage src={profile?.avatar_url || undefined} alt={profile?.username || 'Usuario'} />
                            <AvatarFallback className="text-[10px]">
                              {(profile?.username || 'U').charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        {session?.user?.id && !isOnline && (
                          <div className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground rounded-full p-0.5 border-2 border-primary shadow-sm animate-in zoom-in duration-300">
                            <WifiOff className="w-2.5 h-2.5" />
                          </div>
                        )}
                        <span className="text-sm font-bold text-white max-w-[80px] truncate">
                          {profile?.username || 'Cuenta'}
                        </span>
                        <ChevronDown className="w-3 h-3 text-white/85" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="navbar">Tu cuenta</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Row 2: Nav Links */}
            <div className="flex items-center text-sm pt-1 gap-2">
              <div className="flex items-center min-w-0 flex-1">
                <nav className="flex items-center gap-2 lg:gap-2.5">
                  <div className={cn("relative h-[2.25rem] w-[196px]", isRadioConnecting && "radio-sync-pulse")}>
                    <button
                      type="button"
                      aria-label={isRadioPlaying ? 'Pausar radio' : 'Reproducir radio'}
                      title={isRadioPlaying ? 'Pausar radio' : 'Reproducir radio'}
                      onClick={toggleRadioPlaybackFromIcon}
                      className={cn("radio-antenna-button absolute left-1 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-primary-foreground/15 transition-opacity duration-300", !isRadioPlaying && "opacity-50")}
                    >
                      <AudioLines className="h-4 w-4" />
                    </button>
                    <NavLink to="/radio" className={({isActive}) => cn("text-sm flex items-center justify-start h-full min-w-0 overflow-hidden pl-9 pr-2 flex-shrink-0 rounded-full border transition-all", isRadioActive ? "text-white font-black border-white/35 bg-primary-foreground/20" : "text-white/80 border-white/15 bg-primary-foreground/10 hover:text-white hover:bg-primary-foreground/15")}> 
                      {renderRadioNavLabel(false)}
                    </NavLink>
                  </div>
                  <div className="relative h-[2.25rem] w-[196px]">
                    <button
                      type="button"
                      aria-label="Abrir canasto"
                      title="Abrir canasto"
                      onClick={() => openCheckoutSidebar()}
                      className="absolute left-1 top-1/2 z-10 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-primary-foreground/15"
                    >
                      <ShoppingCart className={cn("h-4 w-4 transition-opacity", !is24hsActive && "opacity-50")} />
                    </button>
                    <NavLink to="/24hs" className={({isActive}) => cn("text-sm inline-flex items-center justify-start h-full w-full pl-9 pr-2 rounded-full border transition-all", is24hsActive ? "text-white font-black border-white/35 bg-primary-foreground/20" : "text-white/80 border-white/15 bg-primary-foreground/10 hover:text-white hover:bg-primary-foreground/15")}> 
                      <span className="block w-full truncate text-center">24hs</span>
                    </NavLink>
                  </div>
                  <div className="relative h-[2.25rem] w-[196px]">
                    <button 
                      onClick={() => openRideRequest('persona', 'traslados')} 
                      className={cn(
                        "h-full w-full text-sm inline-flex items-center justify-center pl-2 pr-2 rounded-full border transition-all whitespace-normal",
                        isTransporteActive ? "font-black border-white/35 bg-primary-foreground/20" : "border-white/15 bg-primary-foreground/10",
                        activeVehicleHasAvailability ? "text-white" : "text-white/45",
                        !isTransporteActive && activeVehicleHasAvailability && "hover:text-white hover:bg-primary-foreground/15"
                      )}
                    >
                      {/* DOCUMENTATION: Changed to match mobile text - shows 'Envíos' for bike-only delivery */}
                      <span className="block w-full truncate text-center">Envíos</span>
                    </button>
                  </div>
                </nav>
              </div>

              <div className="ml-auto flex items-center gap-2">
                <button 
                  onClick={handleHelpClick}
                  className="flex items-center gap-1.5 transition-colors text-white/85 hover:text-white text-sm font-medium"
                  aria-label="Ayuda"
                >
                  <Headset className="h-4 w-4" />
                  Ayuda
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </header>
      
      {/* Safe Area Placeholder for Mobile - Shows when header is hidden on scroll */}
      {!isDesktop && hideHeader && (
        <div 
          className="fixed top-0 left-0 right-0 bg-primary z-50 pointer-events-none"
          style={{ 
            height: 'env(safe-area-inset-top, 0px)'
          }}
        />
      )}
      
      <main 
        ref={mainRef} 
        role="main" 
        className={cn(
          "flex-1 md:pb-6 pb-0",
          "touch-auto", // Asegurar que el scroll táctil sea natural
          // Route-specific backgrounds live here so they sit one layer behind page content
          isMinibid24hsRoute && "bg-background bg-[linear-gradient(180deg,hsl(var(--primary)/0.05)_0%,hsl(var(--primary)/0.0225)_52%,hsl(var(--primary)/0)_98%)] md:bg-[linear-gradient(180deg,hsl(var(--primary)/0.10)_0%,hsl(var(--primary)/0.045)_52%,hsl(var(--primary)/0)_98%)] bg-fixed",
          isFayeRoute && "bg-background bg-[linear-gradient(180deg,hsl(var(--primary)/0.05)_0%,hsl(var(--primary)/0.0225)_52%,hsl(var(--primary)/0)_98%)] md:bg-[linear-gradient(180deg,hsl(var(--primary)/0.10)_0%,hsl(var(--primary)/0.045)_52%,hsl(var(--primary)/0)_98%)] bg-fixed",
          !isMinibid24hsRoute && !isFayeRoute && "bg-transparent",
        )}
        style={{ 
          paddingTop: !isDesktop ? 'var(--app-mobile-top-offset, 0px)' : 0,
          paddingBottom: !isDesktop ? 'var(--app-mobile-bottom-safe-offset, 0px)' : undefined,
        }}
      >
        {(() => {
          const outletKey = getRouteKey(location.pathname);
          return <Outlet key={outletKey} />;
        })()}
      </main>

      {/* Mobile Bottom Navigation */}
      {!isDesktop && (
        <nav 
          className="fixed bottom-[calc(0.5rem+env(safe-area-inset-bottom,0px))] left-2 right-2 sm:left-6 sm:right-6 bg-background/86 dark:bg-background/80 supports-[backdrop-filter]:bg-background/84 backdrop-blur-3xl border border-white/28 dark:border-white/16 px-2 py-2 flex items-center justify-around z-[9000] app-bottom-nav rounded-full shadow-[0_6px_18px_hsl(var(--foreground)/0.07),inset_0_1px_0_hsl(0_0%_100%/0.10)]"
        >
          {/* Main buttons group */}
          <div className="flex items-center justify-around flex-1 gap-1">
            {/* Favoritos primero */}
            <button 
              onClick={() => {
                if (favoritesSidebarOpen) {
                  closeFavoritesSidebar();
                } else {
                  closeNotificationsSidebar();
                  closeUserMenuSidebar();
                  openFavoritesSidebar();
                }
              }}
              className={cn("flex flex-col items-center gap-0.5 w-12 relative", favoritesSidebarOpen ? "text-primary" : "text-muted-foreground")}
            > 
              <Heart className="w-6 h-6" />
              {favorites.size > 0 && (
                <span className={`absolute -top-1 right-1.5 bg-primary/95 supports-[backdrop-filter]:bg-primary/95 backdrop-blur-lg text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full ring-1 ring-inset ring-primary/30 shadow-[0_2px_8px_hsl(var(--foreground)/0.08)] leading-none box-border ${heartPulse ? 'pulse' : ''}`}>
                  {favorites.size > 99 ? '99+' : favorites.size}
                </span>
              )}
              <span className="text-[10px] font-medium">Favoritos</span>
            </button>

            {/* Chat segundo */}
            <button 
              onClick={() => {
                if (chatSidebarOpen) {
                  closeChatSidebar();
                } else {
                  closeNotificationsSidebar();
                  closeUserMenuSidebar();
                  openChatSidebar();
                }
              }}
              className={cn("flex flex-col items-center gap-0.5 w-12 relative", chatSidebarOpen ? "text-primary" : "text-muted-foreground")}
            >
              <MessageSquareText className="w-6 h-6" />
              {chatUnreadCount > 0 && (
                <span className={`absolute -top-1 right-1.5 bg-primary/95 supports-[backdrop-filter]:bg-primary/95 backdrop-blur-lg text-white text-[10px] font-bold min-w-5 h-5 px-1 flex items-center justify-center rounded-full ring-1 ring-inset ring-primary/30 shadow-[0_2px_8px_hsl(var(--foreground)/0.08)] leading-none box-border ${chatPulse ? 'pulse' : ''}`}>
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </span>
              )}
              <span className="text-[10px] font-medium">Chat</span>
            </button>

            <button 
              onClick={() => {
                if (checkoutSidebarOpen) {
                  closeCheckoutSidebar();
                } else {
                  closeChatSidebar();
                  closeNotificationsSidebar();
                  closeUserMenuSidebar();
                  openCheckoutSidebar({ preferredStoreId: preferredStoreId || undefined });
                }
              }}
              className={cn("flex flex-col items-center gap-0.5 w-12 relative", checkoutSidebarOpen ? "text-primary" : "text-muted-foreground")}
            >
              <ShoppingBasket className="w-6 h-6" />
              <span className="text-[10px] font-medium">Pedidos</span>
            </button>

            {/* Actividad cuarto */}
            <button
              onClick={() => {
                if (notificationsSidebarOpen) {
                  closeNotificationsSidebar();
                } else {
                  closeChatSidebar();
                  closeUserMenuSidebar();
                  openNotificationsSidebar();
                }
              }}
              className={cn("flex flex-col items-center gap-0.5 w-12 relative", notificationsSidebarOpen ? "text-primary" : "text-muted-foreground")}
            >
              <Bell className="w-6 h-6" />
              {unreadCount > 0 && (
                <span className={`absolute -top-1 right-1.5 bg-primary/95 supports-[backdrop-filter]:bg-primary/95 backdrop-blur-lg text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full ring-1 ring-inset ring-primary/30 shadow-[0_2px_8px_hsl(var(--foreground)/0.08)] leading-none box-border ${notificationPulse ? 'pulse' : ''}`}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <span className="text-[10px] font-medium">Actividad</span>
            </button>

            <button 
              onClick={() => {
                if (userMenuSidebarOpen) {
                  closeUserMenuSidebar();
                } else {
                  closeNotificationsSidebar();
                  closeChatSidebar();
                  openUserMenuSidebar();
                }
              }}
              className={cn("flex flex-col items-center gap-0.5 w-12 relative", userMenuSidebarOpen ? "text-primary" : "text-muted-foreground")}
            >
              <UserIcon className="w-6 h-6" />
              {!isOnline && (
                <div className="absolute top-0 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 border-2 border-background shadow-sm animate-in zoom-in duration-300">
                  <WifiOff className="w-2.5 h-2.5" />
                </div>
              )}
              <span className="text-[10px] font-medium">Cuenta</span>
            </button>
          </div>
        </nav>
      )}

      {cartSheetOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCartSheetOpen(false)} />
          <div className="ml-auto w-full sm:w-[380px] h-full bg-background shadow-xl flex flex-col p-0 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-primary text-primary-foreground min-h-[64px] shrink-0">
              <div className="font-semibold text-lg">Pedidos</div>
              <button onClick={() => setCartSheetOpen(false)} aria-label="Cerrar" className="p-2 rounded hover:bg-primary-foreground/10 text-primary-foreground transition-colors hover:text-primary-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <CartSidebar />
            </div>
          </div>
        </div>
      )}

      <CitySelectorSidebar
        isOpen={citySidebarOpen}
        onClose={() => setCitySidebarOpen(false)}
        selectedCity={selectedCity}
        onSelectCity={handleCitySelect}
      />

      {/* Payment Sidebar */}
      {paymentSidebarData && session?.user?.id && (
        <>
          <PaymentSidebar
            isOpen={paymentSidebarOpen}
            onClose={() => {
              closePaymentSidebar();
              if (skipNextPaymentCloseReopenRef.current) {
                skipNextPaymentCloseReopenRef.current = false;
                return;
              }
              setTimeout(() => {
                reopenCheckoutSidebar();
              }, 300); // Esperar a que termine la animación de cierre completamente
            }}
            onConfirm={handlePaymentConfirm}
            storeId={paymentSidebarData.storeId}
            storeName={paymentSidebarData.storeName}
            total={paymentSidebarData.total}
            cartId={paymentSidebarData.cartId}
            buyerProfile={buyerBankInfo}
            userId={session.user.id}
            isDigital={paymentSidebarData.isDigital}
            userEmail={session.user.email || undefined}
            userPhoneNumber={profile?.phone_number || undefined}
          />
        </>
      )}

      {rentalStoreAssignment && (
        <div className="fixed inset-0 z-[70] flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (isAssigningRentalStore) return;
              setRentalStoreAssignment(null);
            }}
          />
          <aside className="ml-auto w-full sm:w-[420px] h-full bg-background shadow-xl border-l flex flex-col">
            <div className="px-4 py-4 border-b">
              <h3 className="text-lg font-semibold">Selecciona tu tienda</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Elegi a que tienda asignar el producto rental que acabas de pagar.
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {rentalStoreAssignment.stores.map((store) => {
                const isSelected = rentalStoreAssignment.selectedStoreId === store.id;
                return (
                  <button
                    key={store.id}
                    type="button"
                    onClick={() => setRentalStoreAssignment((prev) => prev ? { ...prev, selectedStoreId: store.id } : prev)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                      isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm">{store.name}</p>
                        <p className="text-xs text-muted-foreground">/{store.slug}</p>
                      </div>
                      {isSelected && <Badge variant="default">Seleccionada</Badge>}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="border-t p-4 flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={isAssigningRentalStore}
                onClick={() => setRentalStoreAssignment(null)}
              >
                Más tarde
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={isAssigningRentalStore || !rentalStoreAssignment.selectedStoreId}
                onClick={handleConfirmRentalStoreAssignment}
              >
                {isAssigningRentalStore ? 'Asignando...' : 'Confirmar tienda'}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Order Sidebar */}
      <OrderSidebar />

      {/* Favorites Sidebar */}
      <FavoritesSidebar 
        isOpen={favoritesSidebarOpen}
        onClose={closeFavoritesSidebar}
        onPlayBeep={playBeep}
        onVibrate={vibrate}
        onAddMultipleItemsToCart={async (items) => {
          
          if (session?.user?.id) {
            // Logged-in: Add all items to same cart
            try {

              // Group items by storeId
              const itemsByStore = new Map<string, typeof items>();
              for (const item of items) {
                if (!itemsByStore.has(item.storeId)) {
                  itemsByStore.set(item.storeId, []);
                }
                itemsByStore.get(item.storeId)!.push(item);
              }
              
              // For each store, get/create ONE cart and add all items
              for (const [storeId, storeItems] of itemsByStore) {
                
                // Get or create cart for this store (ONCE per store)
                const cart = await getOrCreateCartForStore(session.user.id, storeId);
                const cartId = cart.id;
                
                // Add ALL items to this ONE cart
                for (const item of storeItems) {
                  await addCartItem(session.user.id, item.productId, item.quantity, cartId);
                }
              }
              
              window.dispatchEvent(new CustomEvent('cart-updated'));
            } catch (err) {
              console.error('[MainLayout] Error adding multiple items:', err);
              throw err;
            }
          } else {
            // Not logged in: Add to temp cart

            for (const item of items) {
              try {
                const product = await getProductById(item.productId);
                if (product) {
                  addToTempCart({
                    id: product.id,
                    title: product.title,
                    fixed_sale_price: product.fixed_sale_price,
                    currency: 'ARS' as const,
                    stock: product.stock || 999,
                  }, item.quantity, item.storeId);
                }
              } catch (err) {
                console.error('[MainLayout] Error adding to temp cart:', err);
              }
            }
            
            window.dispatchEvent(new CustomEvent('cart-updated'));
          }
        }}
        onAddToCartWithQuantity={(productId, quantity) => {
          
          if (session?.user?.id) {
            // Usuario logueado: agregar al canasto de la BD
            (async () => {
              try {
                // Get product from favorites to get store_id
                const favoriteList = Array.from(favorites.values());
                const product = favoriteList.find(p => p.id === productId);
                
                if (product && product.store_id) {
                  // FIX: Cachear el cartId por store para evitar crear múltiples canastos
                  // cuando se agregan múltiples productos rápidamente
                  let cartId = cartIdCacheRef.current[product.store_id];
                  if (!cartId) {
                    const cart = await getOrCreateCartForStore(session.user.id, product.store_id);
                    cartId = cart.id;
                    cartIdCacheRef.current[product.store_id] = cartId;
                  }
                  
                  // addCartItem already handles summing quantities if item exists
                  // Pass cartId para evitar crear nuevo canasto
                  await addCartItem(session.user.id, productId, quantity, cartId);
                  window.dispatchEvent(new CustomEvent('cart-updated'));
                } else if (product) {
                  console.error('[MainLayout] Product missing store_id:', product);
                }
              } catch (err) {
                console.error('[MainLayout] Error adding to cart for logged-in user:', err);
              }
            })();
          } else {
            // Usuario no logueado: Usar canasto temporal
            try {
              // Obtener el producto desde favoritos
              const favoriteList = Array.from(favorites.values());
              const product = favoriteList.find(p => p.id === productId);
              
              if (product) {
                // Pass quantity directly - addToTempCart handles incrementing
                // DON'T use a loop - it's inefficient and causes multiple updates
                addToTempCart({
                  id: product.id,
                  title: product.title,
                  fixed_sale_price: product.fixed_sale_price,
                  currency: 'ARS' as const,
                  stock: product.stock || 999,
                }, quantity, product.store_id);
                window.dispatchEvent(new CustomEvent('cart-updated'));
              }
            } catch (err) {
              console.error('[MainLayout] Error adding to temp cart:', err);
            }
          }
        }}
      />

      {/* Global Product Sidebar */}
      {selectedProduct && (
        <ProductSidebar
          product={selectedProduct}
          isOpen={productSidebarOpen}
          onClose={() => {
            setProductSidebarOpen(false);
            // We keep selectedProduct for the animation, context handles cleanup if needed
            // but we can also do it here with a timeout
            cleanupTimeoutRef.current = setTimeout(() => {
              setSelectedProduct(null);
              cleanupTimeoutRef.current = null;
            }, 350); // Increased to match sidebar animation duration (300ms) + small buffer
            
            // Reopen order sidebar if product was opened from there
            if (productSidebarOpenedFromOrder) {
              setProductSidebarOpenedFromOrder(false);
              // Small delay to let ProductSidebar fully close
              setTimeout(() => {
                // Trigger order sidebar reopen by dispatching event
                window.dispatchEvent(new CustomEvent('reopen-order-sidebar'));
              }, 350);
            }
          }}
          session={session}
        />
      )}

      {/* Global Note Sidebar */}
      {selectedNote && (
        <NoteSidebar
          note={selectedNote}
          isOpen={noteSidebarOpen}
          onClose={() => {
            setNoteSidebarOpen(false);
            noteCleanupTimeoutRef.current = setTimeout(() => {
              setSelectedNote(null);
              noteCleanupTimeoutRef.current = null;
            }, 350);
          }}
          session={session}
        />
      )}

      {/* Create Note Sidebar */}
      <CreateNoteSidebar />

      {/* Minimized Notes Stack */}
      <MinimizedNotesStack
        notes={openedNotes}
        onNoteClick={async (noteId) => {
          try {
            const fullNote = await getNoteById(noteId);
            if (fullNote) {
              setSelectedNote(fullNote);
              setNoteSidebarOpen(true);
              toggleNoteMinimized(noteId);
            } else {
              console.warn('[MainLayout] getNoteById returned null for:', noteId);
            }
          } catch (e) {
            console.error('[MainLayout] Failed to load note:', e);
          }
        }}
        onNoteClose={(noteId) => {
          toggleNoteMinimized(noteId);
          removeOpenedNote(noteId);
          // Also close the sidebar when closing minimized note
          setNoteSidebarOpen(false);
          setSelectedNote(null);
        }}
      />

      {/* Minimized Products Stack */}
      <MinimizedProductsStack
        products={openedProducts}
        onProductClick={async (productId) => {
          try {
            const fullProduct = await getProductById(productId);
            if (fullProduct) {
              setSelectedProduct(fullProduct);
              setProductSidebarOpen(true);
              toggleProductMinimized(productId);
            } else {
              console.warn('[MainLayout] getProductById returned null for:', productId);
            }
          } catch (e) {
            console.error('[MainLayout] Failed to load product:', e);
          }
        }}
        onProductClose={(productId) => {
          toggleProductMinimized(productId);
          removeOpenedProduct(productId);
          // Also close the sidebar when closing minimized product
          setProductSidebarOpen(false);
          setSelectedProduct(null);
        }}
      />
    </div>
  );
};

export default MainLayout;